import Loading from '../Loading';
import React, { useState, useEffect ,useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import Pagination from './Pagination';
import './ListView.css';
import progressNotesService from '../../services/Progressnotesservice';
import * as XLSX from 'xlsx';
import { renderNoteDetails } from './renderNoteDetails';

const styles = {
  detailsGrid: {
    display: 'grid',
    gap: '15px',
    padding: '20px',
    border: '1px solid #222222ff', 
  },
  detailsRow: {
    display: 'grid',
    gridTemplateColumns: '130px minmax(0, 1fr) 150px minmax(0, 1fr)',
    gap: '10px',
    alignItems: 'start',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  detailsLabel: {
    fontWeight: 'bold',
    color: '#444',
  },
  detailsValue: {
    color: '#666',
    width: '100%',
    minWidth: 0,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    lineHeight: 1.35,
  },
  bodyContent: {
    padding: '10px',
  },
  consentSection: {
    marginBottom: '20px',
    padding: '10px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
  },
  bodySection: {
    margin: '10px 0',
    display: 'flex',
    alignItems: 'flex-start',
  },
  bodyLabel: {
    fontWeight: 'bold',
    marginRight: '10px',
    minWidth: '120px',
    color: '#444',
  },
  bodyValue: {
    flex: 1,
  },
  problemSection: {
    margin: '20px 0',
    padding: '15px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: 'white',
  },
  problemTitle: {
    marginBottom: '10px',
    fontSize: '1.1em',
  },
  goalSection: {
    marginLeft: '20px',
    padding: '10px',
    borderLeft: '3px solid #007bff',
  },
  goalTitle: {
    color: '#007bff',
    fontWeight: 'bold',
    marginBottom: '5px',
  },
  todoTitle: {
    color: '#007bff',
    fontWeight: 'bold',
    marginBottom: '5px',
  },
  goalValue: {
    marginLeft: '10px',
    color: '#555',
  },
  todoValue: {
    marginLeft: '10px',
    color: '#555',
  },
  todoSection: {
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px dashed #ddd',
  },
  errorParsing: {
    color: '#dc3545',
    padding: '10px',
    border: '1px solid #dc3545',
    borderRadius: '4px',
    backgroundColor: '#f8d7da',
  }
};

const ListView = ({ onShowFullText }) => {
  // Removed navigate, use prop for switching view
  const navigate = useNavigate();
  
  const [records, setRecords] = useState([]);
  const [originalRecords, setOriginalRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


    useEffect(() => {
      const fetchProgressNotes = async () => {
        try {
          setLoading(true);
          setError(null);

          // First, call getProgressNotes to get the session data
          const progressResponse = await progressNotesService.getProgressNotes();
          if (!progressResponse || !progressResponse.data) {
            return;
          }

          // Then call loadProgressNotesMaster to get the master data
          const masterResponse = await progressNotesService.loadProgressNotesMaster();
          if (!masterResponse || !masterResponse.data) {
            return;
          }

          // Log the API response for debugging
          console.log('Progress Notes Master Response:', masterResponse.data);

          // Map the master response data to table structure
          const mappedRecords = masterResponse.data.map(note => ({
            id: note.id || '',
            status: note.final === 'Completed' ? '✓' : '', // Green checkmark for completed items
            source: note.source || '',
            category: note.case_Type_Id_Verbose || '',
            registrar: note.registrar || '',
            subject: note.subject || '',
            security: note.security_Level || '',
            caseId: note.tzix_Id || note.case_Id || '',
            patientId: note.patient_Id || '',
            caseName: note.case_Name || '',
            date: note.create_Date ? new Date(note.create_Date).toLocaleString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            }) : '',
            voidReason: note.void_reason || '',
            file_Name: note.file_Name || '' 
          }));

          console.log('Mapped Records:', mappedRecords); // For debugging
          setRecords(mappedRecords);
          setLoading(false);
        } catch (err) {
          if (err.isSessionExpired) {
            // Keep loading spinner visible when session is expired
            return;
          }
          console.error('Error fetching progress notes:', err);
          setLoading(false);
        }
      };

      fetchProgressNotes();
    }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(16);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [columnFilters, setColumnFilters] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(null);
  const [filterValues, setFilterValues] = useState({
    condition1: { operator: 'Is equal to', value: '' },
    condition2: { operator: 'Is equal to', value: '' },
    logicalOperator: 'And'
  });
  const [focusedFilterInput, setFocusedFilterInput] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedNoteDetails, setSelectedNoteDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Backup original records when they change
  useEffect(() => {
    setOriginalRecords(records);
  }, [records]);

  // Apply sorting to data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Calculate pagination
  const sortedData = getSortedData(records);
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Sorting functionality
  const handleSort = (columnKey, direction) => {
    setSortConfig({ key: columnKey, direction });
    setOpenDropdown(null);
  };

  // Toggle dropdown
  const toggleDropdown = (columnKey) => {
    setOpenDropdown(openDropdown === columnKey ? null : columnKey);
  };

  // Apply column filter
  const handleColumnFilter = (columnKey) => {
    setShowFilterPanel(columnKey);
    setOpenDropdown(null);
  };

  // Handle filter value changes
  const handleFilterValueChange = (condition, field, value) => {
    if (condition === 'logicalOperator') {
      setFilterValues(prev => ({
        ...prev,
        logicalOperator: value
      }));
    } else {
      setFilterValues(prev => ({
        ...prev,
        [condition]: {
          ...prev[condition],
          [field]: value
        }
      }));
    }
  };

  // Apply filter with AND/OR logic
  const applyFilter = (columnKey) => {
    const { condition1, condition2, logicalOperator } = filterValues;
    setRecords(prevRecords => {
      return prevRecords.filter(record => {
        const value = record[columnKey] ? record[columnKey].toString().toLowerCase() : '';
        const cond1Value = condition1.value ? condition1.value.toString().toLowerCase() : '';
        const cond2Value = condition2.value ? condition2.value.toString().toLowerCase() : '';

        let cond1 = true;
        let cond2 = true;

        if (cond1Value) {
          switch (condition1.operator) {
            case 'Is equal to':
              cond1 = value === cond1Value;
              break;
            case 'Is not equal to':
              cond1 = value !== cond1Value;
              break;
            case 'Contains':
              cond1 = value.includes(cond1Value);
              break;
            case 'Does not contain':
              cond1 = !value.includes(cond1Value);
              break;
            case 'Starts with':
              cond1 = value.startsWith(cond1Value);
              break;
            case 'Ends with':
              cond1 = value.endsWith(cond1Value);
              break;
            case 'Is greater than':
              cond1 = value > cond1Value;
              break;
            case 'Is less than':
              cond1 = value < cond1Value;
              break;
            default:
              cond1 = true;
          }
        }
        if (cond2Value) {
          switch (condition2.operator) {
            case 'Is equal to':
              cond2 = value === cond2Value;
              break;
            case 'Is not equal to':
              cond2 = value !== cond2Value;
              break;
            case 'Contains':
              cond2 = value.includes(cond2Value);
              break;
            case 'Does not contain':
              cond2 = !value.includes(cond2Value);
              break;
            case 'Starts with':
              cond2 = value.startsWith(cond2Value);
              break;
            case 'Ends with':
              cond2 = value.endsWith(cond2Value);
              break;
            case 'Is greater than':
              cond2 = value > cond2Value;
              break;
            case 'Is less than':
              cond2 = value < cond2Value;
              break;
            default:
              cond2 = true;
          }
        }
        if (!cond1Value && !cond2Value) return true;
        if (logicalOperator === 'And') {
          return cond1 && cond2;
        } else {
          if (!cond1Value) return cond2;
          if (!cond2Value) return cond1;
          return cond1 || cond2;
        }
      });
    });
    setShowFilterPanel(null);
    // Clear selection and reset pagination when filter is applied
    setSelectedNote(null);
    setSelectedNoteDetails(null);
    setCurrentPage(1);
  };

  // Clear filter
  const clearFilter = () => {
    setFilterValues({
      condition1: { operator: 'Is equal to', value: '' },
      condition2: { operator: 'Is equal to', value: '' },
      logicalOperator: 'And'
    });
    setShowFilterPanel(null);
    // Clear selection when filter is cleared
    setSelectedNote(null);
    setSelectedNoteDetails(null);
    setCurrentPage(1);
    // Restore original records
    setRecords(originalRecords);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
      setShowFilterPanel(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Column header dropdown component
  const ColumnDropdown = ({ columnKey, isOpen }) => {
    if (!isOpen) return null;
    
    return (
      <div className="column-dropdown" onClick={(e) => e.stopPropagation()}>
        <div 
          className="dropdown-item"
          onClick={() => handleSort(columnKey, 'asc')}
        >
          <span className="sort-icon">↑</span>
          Sort (asc)
        </div>
        <div 
          className="dropdown-item"
          onClick={() => handleSort(columnKey, 'desc')}
        >
          <span className="sort-icon">↓</span>
          Sort (desc)
        </div>
        <div 
          className="dropdown-item filter-item"
          onClick={() => handleColumnFilter(columnKey)}
        >
          <span className="filter-icon">⚬</span>
          Apply filter
          <span className="filter-arrow">▶</span>
        </div>
      </div>
    );
  };

  // Filter panel component
  const FilterPanel = ({ columnKey, isOpen, filterValues, handleFilterValueChange, focusedFilterInput, setFocusedFilterInput }) => {
    const condition1Ref = useRef(null);
    const condition2Ref = useRef(null);

    useEffect(() => {
      if (focusedFilterInput === 'condition1' && condition1Ref.current) {
        condition1Ref.current.focus();
      } else if (focusedFilterInput === 'condition2' && condition2Ref.current) {
        condition2Ref.current.focus();
      }
    }, [focusedFilterInput]);
    if (!isOpen) return null;

    const operators = [
      'Is equal to',
      'Is not equal to',
      'Contains',
      'Does not contain',
      'Starts with',
      'Ends with',
      'Is greater than',
      'Is less than'
    ];

    return (
      <div className="filter-panel" onClick={(e) => e.stopPropagation()}>
        <div className="filter-header">
          <span className="filter-icon">⚬</span>
          Apply filter
        </div>

        <div className="filter-content">
          <div className="filter-instruction">Show items with value that:</div>
          <div className="filter-condition" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <select
              value={filterValues.condition1.operator}
              onChange={(e) => handleFilterValueChange('condition1', 'operator', e.target.value)}
              className="filter-operator"
            >
              {operators.map(op => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
            <input
              ref={condition1Ref}
              type="text"
              value={filterValues.condition1.value}
              onChange={(e) => handleFilterValueChange('condition1', 'value', e.target.value)}
              className="filter-input"
              placeholder="Enter value"
              onFocus={() => setFocusedFilterInput('condition1')}
              onClick={() => setFocusedFilterInput('condition1')}
              style={{ marginTop: '0px' }}
            />
          </div>

          <div className="filter-logical">
            <select
              value={filterValues.logicalOperator}
              onChange={(e) => handleFilterValueChange('logicalOperator', '', e.target.value)}
              className="filter-logical-operator"
            >
              <option value="And">And</option>
              <option value="Or">Or</option>
            </select>
          </div>

          <div className="filter-condition" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <select
              value={filterValues.condition2.operator}
              onChange={(e) => handleFilterValueChange('condition2', 'operator', e.target.value)}
              className="filter-operator"
            >
              {operators.map(op => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
            <input
              ref={condition2Ref}
              type="text"
              value={filterValues.condition2.value}
              onChange={(e) => handleFilterValueChange('condition2', 'value', e.target.value)}
              className="filter-input"
              placeholder="Enter value"
              onFocus={() => setFocusedFilterInput('condition2')}
              onClick={() => setFocusedFilterInput('condition2')}
              style={{ marginTop: '0px' }}
            />
          </div>

          <div className="filter-actions">
            <button
              className="filter-btn apply-btn"
              onClick={() => applyFilter(columnKey)}
            >
              Filter
            </button>
            <button
              className="filter-btn clear-btn"
              onClick={clearFilter}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Use prop to switch to FullTextView
  const handleFullTextView = () => {
    if (typeof onShowFullText === 'function') {
      onShowFullText();
    }
  };

  const handleExportToExcel = () => {
    // Prepare the data for Excel export
    const excelData = records.map(record => ({
      Status: record.status,
      Source: record.source,
      Category: record.category,
      Registrar: record.registrar,
      Subject: record.subject,
      Security: record.security,
      'Case ID': record.caseId,
      'Case Name': record.caseName,
      Date: record.date,
      'Void Reason': record.voidReason
    }));

    // Create a new workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Progress Notes');

    // Generate Excel file buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create a Blob from the buffer
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Create download link and trigger download
    const fileName = `ProgressNotes.xlsx`;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
  };

  // Handle row click to fetch and display details
  const handleRowClick = async (record) => {
    try {
      setSelectedNote(record);
      setLoadingDetails(true);
      const details = await progressNotesService.getSelectedRowDetails(record.id);
      // If details is an array, use the first item; otherwise, use as is
      setSelectedNoteDetails(details && Array.isArray(details) ? details[0] : details);
    } catch (error) {
      console.error('Error fetching note details:', error);
      setSelectedNoteDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <div className="list-view-container">
      {loading && <Loading />}
      {/* Header with buttons */}
      <div className="header-section">
        <div className="header-buttons">
          <button 
            className="full-text-btn" 
            onClick={handleFullTextView}
           // disabled={loading || records.length === 0}
          >
            Full Text View
          </button>
          <button 
            className="export-excel-btn" 
            onClick={handleExportToExcel}
            disabled={loading || records.length === 0}
          >
            <span className="excel-icon" style={{ marginRight: 6, display: 'inline-flex', alignItems: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" fill="#fff" stroke="#bbb" strokeWidth="1"/>
                <rect x="3" y="3" width="18" height="5" fill="#e5f1e5" stroke="#bbb" strokeWidth="1"/>
                <text x="8" y="16" fontSize="10" fill="#1D6F42" fontFamily="Arial" fontWeight="bold">X</text>
              </svg>
            </span>
            Export to Excel
          </button>
        </div>
      </div>
      
      {/* Table */}
      <div className="table-container">
        <table className="records-table">
          <thead>
            <tr>
              {/* Status */}
              <th className={`sortable-header${sortConfig.key === 'status' ? ' sorted-header' : ''}`} style={{ position: 'relative' }}>
                <div className="header-label-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 20, position: 'relative', color: '#1565c0', fontWeight: 'bold', minHeight: '32px', padding: '0 8px' }}>
                  <span style={{ cursor: 'pointer', display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }} onClick={(e) => { e.stopPropagation(); setSortConfig(prev => prev.key === 'status' ? { key: 'status', direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key: 'status', direction: 'asc' }); setOpenDropdown(null); }}>
                    Status
                    {sortConfig.key === 'status' && (
                      <span className="sort-indicator" style={{ marginLeft: 4, color: '#fff' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                  <span className="dropdown-icon" onClick={(e) => { e.stopPropagation(); toggleDropdown('status'); }} style={{ cursor: 'pointer' }}>▼</span>
                </div>
                <div style={{ position: 'absolute', left: 0, top: '36px', width: '100%', zIndex: 10 }}>
                  {openDropdown === 'status' && <ColumnDropdown columnKey="status" isOpen={true} />}
                  {showFilterPanel === 'status' && (
                    <FilterPanel
                      columnKey="status"
                      isOpen={true}
                      filterValues={filterValues}
                      handleFilterValueChange={handleFilterValueChange}
                      focusedFilterInput={focusedFilterInput}
                      setFocusedFilterInput={setFocusedFilterInput}
                    />
                  )}
                </div>
              </th>
              <th style={{ width: '40px' }}></th>
              {/* Source */}
              <th className={`sortable-header${sortConfig.key === 'source' ? ' sorted-header' : ''}`} style={{ position: 'relative' }}>
                <div className="header-label-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 20, position: 'relative', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', minHeight: '32px', padding: '0 8px' }}>
                  <span style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setSortConfig(prev => prev.key === 'source' ? { key: 'source', direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key: 'source', direction: 'asc' }); setOpenDropdown(null); }}>
                    Source
                    {sortConfig.key === 'source' && (
                      <span className="sort-indicator" style={{ marginLeft: 4, color: '#fff' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                  <span className="dropdown-icon" onClick={(e) => { e.stopPropagation(); toggleDropdown('source'); }} style={{ cursor: 'pointer' }}>▼</span>
                </div>
                <div style={{ position: 'absolute', left: 0, top: '36px', width: '100%', zIndex: 10 }}>
                  {openDropdown === 'source' && <ColumnDropdown columnKey="source" isOpen={true} />}
                  {showFilterPanel === 'source' && (
                    <FilterPanel
                      columnKey="source"
                      isOpen={true}
                      filterValues={filterValues}
                      handleFilterValueChange={handleFilterValueChange}
                      focusedFilterInput={focusedFilterInput}
                      setFocusedFilterInput={setFocusedFilterInput}
                    />
                  )}
                </div>
              </th>
              {/* Category */}
              <th className={`sortable-header${sortConfig.key === 'category' ? ' sorted-header' : ''}`} style={{ position: 'relative' }}>
                <div className="header-label-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 20, position: 'relative', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', minHeight: '32px', padding: '0 8px' }}>
                  <span style={{ cursor: 'pointer', display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }} onClick={(e) => { e.stopPropagation(); setSortConfig(prev => prev.key === 'category' ? { key: 'category', direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key: 'category', direction: 'asc' }); setOpenDropdown(null); }}>
                    Category
                    {sortConfig.key === 'category' && (
                      <span className="sort-indicator" style={{ marginLeft: 4, color: '#fff' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                  <span className="dropdown-icon" onClick={(e) => { e.stopPropagation(); toggleDropdown('category'); }} style={{ cursor: 'pointer' }}>▼</span>
                </div>
                <div style={{ position: 'absolute', left: 0, top: '36px', width: '100%', zIndex: 10 }}>
                  {openDropdown === 'category' && <ColumnDropdown columnKey="category" isOpen={true} />}
                  {showFilterPanel === 'category' && (
                    <FilterPanel
                      columnKey="category"
                      isOpen={true}
                      filterValues={filterValues}
                      handleFilterValueChange={handleFilterValueChange}
                      focusedFilterInput={focusedFilterInput}
                      setFocusedFilterInput={setFocusedFilterInput}
                    />
                  )}
                </div>
              </th>
              {/* Registrar */}
              <th className={`sortable-header${sortConfig.key === 'registrar' ? ' sorted-header' : ''}`} style={{ position: 'relative' }}>
                <div className="header-label-wrapper">
                  <span style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setSortConfig(prev => prev.key === 'registrar' ? { key: 'registrar', direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key: 'registrar', direction: 'asc' }); setOpenDropdown(null); }}>
                    Registrar
                    {sortConfig.key === 'registrar' && (
                      <span className="sort-indicator" style={{ marginLeft: 4, color: '#fff' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                  <span className="dropdown-icon" onClick={(e) => { e.stopPropagation(); toggleDropdown('registrar'); }} style={{ cursor: 'pointer' }}>▼</span>
                </div>
                <div style={{ position: 'absolute', left: 0, top: '36px', width: '100%', zIndex: 10 }}>
                  {openDropdown === 'registrar' && <ColumnDropdown columnKey="registrar" isOpen={true} />}
                  {showFilterPanel === 'registrar' && (
                    <FilterPanel
                      columnKey="registrar"
                      isOpen={true}
                      filterValues={filterValues}
                      handleFilterValueChange={handleFilterValueChange}
                      focusedFilterInput={focusedFilterInput}
                      setFocusedFilterInput={setFocusedFilterInput}
                    />
                  )}
                </div>
              </th>
              {/* Subject */}
              <th className={`sortable-header${sortConfig.key === 'subject' ? ' sorted-header' : ''}`} style={{ position: 'relative', width: '200px', minWidth: '200px', maxWidth: '200px', overflow: 'visible', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <div className="header-label-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 20, position: 'relative', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', minHeight: '32px', padding: '0 8px' }}>
                  <span style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setSortConfig(prev => prev.key === 'subject' ? { key: 'subject', direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key: 'subject', direction: 'asc' }); setOpenDropdown(null); }}>
                    Subject
                    {sortConfig.key === 'subject' && (
                      <span className="sort-indicator" style={{ marginLeft: 4, color: '#fff' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                  <span className="dropdown-icon" onClick={(e) => { e.stopPropagation(); toggleDropdown('subject'); }} style={{ cursor: 'pointer' }}>▼</span>
                </div>
                <div style={{ position: 'absolute', left: 0, top: '36px', width: '100%', zIndex: 10 }}>
                  {openDropdown === 'subject' && <ColumnDropdown columnKey="subject" isOpen={true} />}
                  {showFilterPanel === 'subject' && (
                    <FilterPanel
                      columnKey="subject"
                      isOpen={true}
                      filterValues={filterValues}
                      handleFilterValueChange={handleFilterValueChange}
                      focusedFilterInput={focusedFilterInput}
                      setFocusedFilterInput={setFocusedFilterInput}
                    />
                  )}
                </div>
              </th>
              {/* Security */}
              <th className={`sortable-header${sortConfig.key === 'security' ? ' sorted-header' : ''}`} style={{ position: 'relative' }}>
                <div className="header-label-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 20, position: 'relative', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', minHeight: '32px', padding: '0 8px' }}>
                  <span style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setSortConfig(prev => prev.key === 'security' ? { key: 'security', direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key: 'security', direction: 'asc' }); setOpenDropdown(null); }}>
                    Security
                    {sortConfig.key === 'security' && (
                      <span className="sort-indicator" style={{ marginLeft: 4, color: '#fff' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                  <span className="dropdown-icon" onClick={(e) => { e.stopPropagation(); toggleDropdown('security'); }} style={{ cursor: 'pointer' }}>▼</span>
                </div>
                <div style={{ position: 'absolute', left: 0, top: '36px', width: '100%', zIndex: 10 }}>
                  {openDropdown === 'security' && <ColumnDropdown columnKey="security" isOpen={true} />}
                  {showFilterPanel === 'security' && (
                    <FilterPanel
                      columnKey="security"
                      isOpen={true}
                      filterValues={filterValues}
                      handleFilterValueChange={handleFilterValueChange}
                      focusedFilterInput={focusedFilterInput}
                      setFocusedFilterInput={setFocusedFilterInput}
                    />
                  )}
                </div>
              </th>
              {/* Case ID */}
              <th className={`sortable-header${sortConfig.key === 'caseId' ? ' sorted-header' : ''}`} style={{ position: 'relative' }}>
                <div className="header-label-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 20, position: 'relative', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', minHeight: '32px', padding: '0 8px' }}>
                  <span style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setSortConfig(prev => prev.key === 'caseId' ? { key: 'caseId', direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key: 'caseId', direction: 'asc' }); setOpenDropdown(null); }}>
                    Case ID
                    {sortConfig.key === 'caseId' && (
                      <span className="sort-indicator" style={{ marginLeft: 4, color: '#fff' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                  <span className="dropdown-icon" onClick={(e) => { e.stopPropagation(); toggleDropdown('caseId'); }} style={{ cursor: 'pointer' }}>▼</span>
                </div>
                <div style={{ position: 'absolute', left: 0, top: '36px', width: '100%', zIndex: 10 }}>
                  {openDropdown === 'caseId' && <ColumnDropdown columnKey="caseId" isOpen={true} />}
                  {showFilterPanel === 'caseId' && (
                    <FilterPanel
                      columnKey="caseId"
                      isOpen={true}
                      filterValues={filterValues}
                      handleFilterValueChange={handleFilterValueChange}
                      focusedFilterInput={focusedFilterInput}
                      setFocusedFilterInput={setFocusedFilterInput}
                    />
                  )}
                </div>
              </th>
              {/* Case Name */}
              <th className={`sortable-header${sortConfig.key === 'caseName' ? ' sorted-header' : ''}`} style={{ position: 'relative' }}>
                <div className="header-label-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 20, position: 'relative', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', minHeight: '32px', padding: '0 8px' }}>
                  <span style={{ cursor: 'pointer', display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }} onClick={(e) => { e.stopPropagation(); setSortConfig(prev => prev.key === 'caseName' ? { key: 'caseName', direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key: 'caseName', direction: 'asc' }); setOpenDropdown(null); }}>
                    Case Name
                    {sortConfig.key === 'caseName' && (
                      <span className="sort-indicator" style={{ marginLeft: 4, color: '#fff' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                  <span className="dropdown-icon" onClick={(e) => { e.stopPropagation(); toggleDropdown('caseName'); }} style={{ cursor: 'pointer' }}>▼</span>
                </div>
                <div style={{ position: 'absolute', left: 0, top: '36px', width: '100%', zIndex: 10 }}>
                  {openDropdown === 'caseName' && <ColumnDropdown columnKey="caseName" isOpen={true} />}
                  {showFilterPanel === 'caseName' && (
                    <FilterPanel
                      columnKey="caseName"
                      isOpen={true}
                      filterValues={filterValues}
                      handleFilterValueChange={handleFilterValueChange}
                      focusedFilterInput={focusedFilterInput}
                      setFocusedFilterInput={setFocusedFilterInput}
                    />
                  )}
                </div>
              </th>
              {/* Date */}
              <th className={`sortable-header${sortConfig.key === 'date' ? ' sorted-header' : ''}`} style={{ position: 'relative' }}>
                <div className="header-label-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 20, position: 'relative', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', minHeight: '32px', padding: '0 8px' }}>
                  <span style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setSortConfig(prev => prev.key === 'date' ? { key: 'date', direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key: 'date', direction: 'asc' }); setOpenDropdown(null); }}>
                    Date
                    {sortConfig.key === 'date' && (
                      <span className="sort-indicator" style={{ marginLeft: 4, color: '#fff' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                  <span className="dropdown-icon" onClick={(e) => { e.stopPropagation(); toggleDropdown('date'); }} style={{ cursor: 'pointer' }}>▼</span>
                </div>
                <div style={{ position: 'absolute', left: 0, top: '36px', width: '100%', zIndex: 10 }}>
                  {openDropdown === 'date' && <ColumnDropdown columnKey="date" isOpen={true} />}
                  {showFilterPanel === 'date' && (
                    <FilterPanel
                      columnKey="date"
                      isOpen={true}
                      filterValues={filterValues}
                      handleFilterValueChange={handleFilterValueChange}
                      focusedFilterInput={focusedFilterInput}
                      setFocusedFilterInput={setFocusedFilterInput}
                    />
                  )}
                </div>
              </th>
              {/* Void Reason */}
              <th className={`sortable-header${sortConfig.key === 'voidReason' ? ' sorted-header' : ''}`} style={{ position: 'relative' }}>
                <div className="header-label-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 20, position: 'relative', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', minHeight: '32px', padding: '0 8px' }}>
                  <span style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setSortConfig(prev => prev.key === 'voidReason' ? { key: 'voidReason', direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key: 'voidReason', direction: 'asc' }); setOpenDropdown(null); }}>
                    Void Reason
                    {sortConfig.key === 'voidReason' && (
                      <span className="sort-indicator" style={{ marginLeft: 4, color: '#fff' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                  <span className="dropdown-icon" onClick={(e) => { e.stopPropagation(); toggleDropdown('voidReason'); }} style={{ cursor: 'pointer' }}>▼</span>
                </div>
                <div style={{ position: 'absolute', left: 0, top: '36px', width: '100%', zIndex: 10 }}>
                  {openDropdown === 'voidReason' && <ColumnDropdown columnKey="voidReason" isOpen={true} />}
                  {showFilterPanel === 'voidReason' && (
                    <FilterPanel
                      columnKey="voidReason"
                      isOpen={true}
                      filterValues={filterValues}
                      handleFilterValueChange={handleFilterValueChange}
                      focusedFilterInput={focusedFilterInput}
                      setFocusedFilterInput={setFocusedFilterInput}
                    />
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr>
                <td colSpan="10" className="error-data">{error}</td>
              </tr>
            ) : currentItems.length > 0 ? (
              currentItems.map((record) => (
                <tr 
                  key={record.id}
                  onClick={() => handleRowClick(record)}
                  className={selectedNote?.id === record.id ? 'selected-row' : ''}
                  style={{ cursor: 'pointer', backgroundColor: selectedNote?.id === record.id ? '#1598c079' : undefined, color: selectedNote?.id === record.id ? '#fff' : undefined }}
                >
                  <td className="status-cell">
                    <span className="status-check" style={{ color: '#4CAF50' }}>{record.status}</span>
                  </td>
                    <td style={{ textAlign: 'center' }}>
                      {record.file_Name ? (
                        <span
                          title="Attachment"
                          style={{ cursor: 'pointer', display: 'inline-block' }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            handleRowClick(record);
                            try {
                              // Use patientId and notepadId from record
                              const fileDownloaded = await progressNotesService.checkFileExists(
                                record.file_Name,
                                record.patientId,
                                record.id
                              );
                              if (!fileDownloaded) {
                                alert('File Doesnot Exist');
                              }
                            } catch (err) {
                              alert('File Doesnot Exist');
                            }
                              // Also select the row and show body data
                               handleRowClick(record);
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#504d59ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-225deg)' }}>
                            <path d="M21.44 11.05a5.5 5.5 0 0 0-7.78-7.78l-8.49 8.49a5.5 5.5 0 0 0 7.78 7.78l7.07-7.07a3.5 3.5 0 0 0-4.95-4.95l-6.36 6.36" />
                          </svg>
                        </span>
                      ) : null}
                    </td>
                  <td>{record.source}</td>
                  <td>{record.category}</td>
                  <td
                    style={{ width: '130px', minWidth: '130px', maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'relative' }}
                  >
                    <span
                      className="truncated-cell"
                      style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
                      onMouseEnter={e => {
                        const tooltip = document.createElement('div');
                        tooltip.className = 'custom-tooltip';
                        tooltip.innerText = record.registrar;
                        tooltip.style.position = 'fixed';
                        tooltip.style.background = '#fff';
                        tooltip.style.color = '#000000';
                        tooltip.style.padding = '6px 12px';
                        tooltip.style.borderRadius = '4px';
                        tooltip.style.border = '1px solid #ccc';
                        tooltip.style.fontSize = '13px';
                        tooltip.style.zIndex = '9999';
                        tooltip.style.maxWidth = '400px';
                        tooltip.style.wordBreak = 'break-word';
                        document.body.appendChild(tooltip);
                        const rect = e.target.getBoundingClientRect();
                        tooltip.style.top = `${rect.bottom + 6}px`;
                        tooltip.style.left = `${rect.left}px`;
                        e.target._tooltip = tooltip;
                      }}
                      onMouseLeave={e => {
                        if (e.target._tooltip) {
                          document.body.removeChild(e.target._tooltip);
                          e.target._tooltip = null;
                        }
                      }}
                    >
                      {record.registrar}
                    </span>
                  </td>
                  <td
                    style={{ width: '200px', minWidth: '200px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'relative' }}
                  >
                    <span
                      className="truncated-cell"
                      style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
                      onMouseEnter={e => {
                        const tooltip = document.createElement('div');
                        tooltip.className = 'custom-tooltip';
                        tooltip.innerText = record.subject;
                        tooltip.style.position = 'fixed';
                        tooltip.style.background = '#fff';
                        tooltip.style.color = ' #000000';
                        tooltip.style.padding = '6px 12px';
                        tooltip.style.borderRadius = '4px';
                         tooltip.style.border='1px solid #ccc';
                        tooltip.style.fontSize = '13px';
                        tooltip.style.zIndex = '9999';
                        tooltip.style.maxWidth = '400px';
                        tooltip.style.wordBreak = 'break-word';
                        document.body.appendChild(tooltip);
                        const rect = e.target.getBoundingClientRect();
                        tooltip.style.top = `${rect.bottom + 6}px`;
                        tooltip.style.left = `${rect.left}px`;
                        e.target._tooltip = tooltip;
                      }}
                      onMouseLeave={e => {
                        if (e.target._tooltip) {
                          document.body.removeChild(e.target._tooltip);
                          e.target._tooltip = null;
                        }
                      }}
                    >
                      {record.subject}
                    </span>
                  </td>
                  <td>{record.security}</td>
                  <td>{record.caseId}</td>
                  <td
                    style={{ width: '80px', minWidth: '80px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'relative' }}
                  >
                    <span
                      className="truncated-cell"
                      style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
                      onMouseEnter={e => {
                        const tooltip = document.createElement('div');
                        tooltip.className = 'custom-tooltip';
                        tooltip.innerText = record.caseName;
                        tooltip.style.position = 'fixed';
                        tooltip.style.background = '#333';
                        tooltip.style.color = '#fff';
                        tooltip.style.padding = '6px 12px';
                        tooltip.style.borderRadius = '4px';
                        tooltip.style.fontSize = '13px';
                        tooltip.style.zIndex = '9999';
                        tooltip.style.maxWidth = '400px';
                        tooltip.style.wordBreak = 'break-word';
                        document.body.appendChild(tooltip);
                        const rect = e.target.getBoundingClientRect();
                        tooltip.style.top = `${rect.bottom + 6}px`;
                        tooltip.style.left = `${rect.left}px`;
                        e.target._tooltip = tooltip;
                      }}
                      onMouseLeave={e => {
                        if (e.target._tooltip) {
                          document.body.removeChild(e.target._tooltip);
                          e.target._tooltip = null;
                        }
                      }}
                    >
                      {record.caseName}
                    </span>
                  </td>
                  <td>{record.date}</td>
                  <td>{record.voidReason}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="no-data">
                  No items to display
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination 
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={sortedData.length}
      />

      {/* Progress Note Details Section */}
      {selectedNote && (
        <div className="progress-note-details">
          {/* <button 
            className="export-pdf-btn" 
            onClick={async () => {
              try {
                if (selectedNote && selectedNote.id) {
                  await progressNotesService.exportSelectedToPDF(selectedNote.id);
                }
              } catch (error) {
                console.error('Error exporting PDF:', error);
                // You could add a user notification here for the error
              }
            }}
            disabled={loadingDetails || !selectedNote}
          >
            Export PDF
          </button> */}
          <button 
  className="export-pdf-btn" 
  
  onClick={async () => {
    setLoadingDetails(true);
    try {
      if (selectedNote && selectedNote.id) {
        // Get the HTML content of the note details section
        const noteDetailsElement = document.querySelector('.note-details-content');
        let htmlContent = '';
        if (noteDetailsElement) {
          htmlContent = noteDetailsElement.innerHTML;
        } else {
          htmlContent = '<div>No details available</div>';
        }
        await progressNotesService.exportSelectedToPDF(selectedNote.id, htmlContent);
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      // You could add a user notification here for the error
    }
    finally {
      setLoadingDetails(false);
    }
  }}
  disabled={loadingDetails || !selectedNote}
>
  Export PDF
</button>
          {/* <h3 className="details-title">Progress Note Details</h3> */}
          {loadingDetails ? (
            <Loading />
          ) : selectedNoteDetails ? (
            <div className="note-details-content" style={{  background: '#fff', padding: 24, marginBottom: 24 }}>
              <h3 className="details-title" style={{ textAlign: 'left', marginLeft: 0, color: '#333' }}>Progress Note Details</h3>
              <div style={styles.detailsGrid}>
                <div style={styles.detailsRow}>
                  <div style={styles.detailsLabel}>Source :</div>
                  <div style={styles.detailsValue}>{selectedNoteDetails.source}</div>
                  <div style={styles.detailsLabel}>Date :</div>
                  <div style={styles.detailsValue}>
                    {selectedNoteDetails.create_Date ? 
                      new Date(selectedNoteDetails.create_Date).toLocaleString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      }) : ''}
                  </div>
                </div>
                <div style={styles.detailsRow}>
                  <div style={styles.detailsLabel}>Subject :</div>
                  <div style={styles.detailsValue}>{selectedNoteDetails.subject}</div>
                  <div style={styles.detailsLabel}>Registrar :</div>
                  <div style={styles.detailsValue}>{selectedNoteDetails.registrar}</div>
                </div>
                <div style={styles.detailsRow}>
                  <div style={styles.detailsLabel}>Case Id :</div>
                  <div style={styles.detailsValue}>
                    {selectedNoteDetails.tzix_Id || selectedNoteDetails.case_Id}
                    {selectedNoteDetails.case_Name ? ` - ${selectedNoteDetails.case_Name}` : ''}
                  </div>
                  <div style={styles.detailsLabel}>Security_Level :</div>
                  <div style={styles.detailsValue}>{selectedNoteDetails.security_Level}</div>
                </div>
                <div style={{ width: '100%', gridColumn: '1 / -1', textAlign: 'left' }}>
                  <div style={{ ...styles.detailsLabel, textAlign: 'left' }}>Body :</div>
                  <div style={{ ...styles.bodyContent, width: '100%', textAlign: 'left' }}>
                    {renderNoteDetails(selectedNoteDetails)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-details">No details available</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ListView;
